import { useState, useEffect } from "react";
import { Calendar, Clock, Rocket } from "lucide-react";

export default function CountdownTimer({ launchDate, projectName, compact = false }) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isLaunched, setIsLaunched] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const launch = new Date(launchDate).getTime();
      const difference = launch - now;

      if (difference <= 0) {
        setIsLaunched(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [launchDate]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  if (compact) {
    if (isLaunched) {
      return (
        <div className="flex items-center gap-2 text-emerald-600">
          <Rocket className="w-4 h-4" />
          <span className="text-sm font-medium">Launched!</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3" data-testid={`countdown-compact-${projectName}`}>
        <Clock className="w-4 h-4 text-slate-400" />
        <div className="flex items-center gap-1 text-sm">
          <span className="font-mono font-bold text-[#0F2744]">{timeLeft.days}</span>
          <span className="text-slate-400">d</span>
          <span className="font-mono font-bold text-[#0F2744]">{String(timeLeft.hours).padStart(2, '0')}</span>
          <span className="text-slate-400">h</span>
          <span className="font-mono font-bold text-[#0F2744]">{String(timeLeft.minutes).padStart(2, '0')}</span>
          <span className="text-slate-400">m</span>
          <span className="font-mono font-bold text-[#0F2744]">{String(timeLeft.seconds).padStart(2, '0')}</span>
          <span className="text-slate-400">s</span>
        </div>
      </div>
    );
  }

  if (isLaunched) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center" data-testid={`countdown-${projectName}`}>
        <Rocket className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-emerald-700">Project Launched!</h3>
        <p className="text-emerald-600 mt-1">Investment is now open</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-[#0F2744] to-[#1E3A5F] rounded-xl p-6" data-testid={`countdown-${projectName}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-white/60 text-sm">Launching</p>
          <h3 className="text-white font-semibold">{projectName}</h3>
        </div>
        <div className="flex items-center gap-2 text-white/60">
          <Calendar className="w-4 h-4" />
          <span className="text-sm">{formatDate(launchDate)}</span>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white/10 rounded-lg p-3 text-center">
          <p className="text-3xl font-bold text-white font-mono">{timeLeft.days}</p>
          <p className="text-white/60 text-xs uppercase tracking-wide">Days</p>
        </div>
        <div className="bg-white/10 rounded-lg p-3 text-center">
          <p className="text-3xl font-bold text-white font-mono">{String(timeLeft.hours).padStart(2, '0')}</p>
          <p className="text-white/60 text-xs uppercase tracking-wide">Hours</p>
        </div>
        <div className="bg-white/10 rounded-lg p-3 text-center">
          <p className="text-3xl font-bold text-white font-mono">{String(timeLeft.minutes).padStart(2, '0')}</p>
          <p className="text-white/60 text-xs uppercase tracking-wide">Mins</p>
        </div>
        <div className="bg-white/10 rounded-lg p-3 text-center">
          <p className="text-3xl font-bold text-white font-mono">{String(timeLeft.seconds).padStart(2, '0')}</p>
          <p className="text-white/60 text-xs uppercase tracking-wide">Secs</p>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center justify-center gap-2 text-white/80">
          <Rocket className="w-4 h-4" />
          <span className="text-sm">Token sale begins at launch</span>
        </div>
      </div>
    </div>
  );
}
